import type { ReactElement, ReactNode } from 'react';

/**
 * The gradient-emphasis pair — the one phrase a headline paints in the brand
 * ramp, and the placeholder that lets each locale put it where its grammar
 * wants it.
 *
 * Split out of `typography.tsx` when that file crossed the 300-line ceiling
 * (`.claude/rules/architecture.md`). It is the seam the file already had: the
 * two components here are the only ones that paint rather than merely set
 * type, they share `GradientTextProps`, and neither imports anything from the
 * rest of the scale — so the edge is one-directional and nothing else moved.
 * `typography.tsx` re-exports both, so no call site changed.
 */

export interface GradientTextProps {
  /**
   * Keep the phrase on one line — the design does this for the hero, so the
   * gradient reads as one sweep rather than restarting on each line fragment.
   *
   * It applies from `sm` UP only. An unconditional `whitespace-nowrap` on a
   * 40px headline phrase is a promise the longest locale cannot keep: Spanish's
   * "que se construye sola" measures 382px in a 350px column at 390px and
   * pushed the landing page sideways, where English's "that builds itself"
   * fitted. `ui-design.md` § 7 — check the width in the longest locale, not in
   * English.
   */
  readonly nowrap?: boolean;
  /** Paint the band ramp rather than the light-page one. See `GradientText`. */
  readonly onDark?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * Brand-gradient text, for the one emphasised phrase in a headline.
 *
 * The colour fallback and the forced-colors override live in `bedrock.css`
 * (`.bd-grad-text` / `.bd-grad-text-dark`) rather than here, because
 * `background-clip: text` needs a real `@supports` guard and Tailwind cannot
 * express one.
 *
 * The two grounds paint DIFFERENT ramps, and neither is readable on the other's
 * ground — measured in `bedrock-contrast.test.ts`. `onDark` is therefore not a
 * styling nicety: getting it wrong produces a headline at 2.86:1.
 */
export function GradientText({
  nowrap = false,
  onDark = false,
  className,
  children,
}: GradientTextProps): ReactElement {
  return (
    <span
      className={[
        onDark ? 'bd-grad-text-dark' : 'bd-grad-text',
        nowrap ? 'sm:whitespace-nowrap' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Accented                                                            */
/* ------------------------------------------------------------------ */

export interface AccentedProps {
  /** A translated sentence containing the literal token `{accent}`. */
  readonly text: string;
  /** The translated phrase that replaces the token, painted in the gradient. */
  readonly accent: string;
  readonly nowrap?: boolean;
  /** Forwarded to `GradientText` — the ramp is ground-specific. */
  readonly onDark?: boolean;
}

/**
 * A headline with ONE gradient-emphasised phrase inside it.
 *
 * The emphasis is expressed as a `{accent}` placeholder in the sentence rather
 * than as three separate before/accent/after keys, because word ORDER moves
 * between the six locales: German pushes the participle to the end, Arabic and
 * Hebrew read right-to-left, and a fixed before/after pair silently mistranslates
 * all three. A placeholder lets each catalog put the phrase where its grammar
 * wants it.
 *
 * A catalog that omits the token degrades to the plain sentence — the headline
 * still reads correctly, it just loses its emphasis, which is the right way for
 * a decoration to fail.
 */
export function Accented({
  text,
  accent,
  nowrap = true,
  onDark = false,
}: AccentedProps): ReactElement {
  const [before, ...rest] = text.split('{accent}');
  if (rest.length === 0) return <>{text}</>;
  return (
    <>
      {before}
      <GradientText nowrap={nowrap} onDark={onDark}>
        {accent}
      </GradientText>
      {rest.join('{accent}')}
    </>
  );
}
